<script>
    import { onMount, tick } from "svelte";

    import { preOpQuestionnaires } from "../../../stores";

    import { createEventDispatcher } from "svelte";

    import {
        checkAbnValidation,
        getAmountInCurrency,
        getEmailValidation,
        getLandlineValidation,
        getPhoneValidation,
        getText,
        getTollFreeValidation,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        CardLayout,
        DecimalInput,
        Loader,
        NumericInput,
        PercentageInput,
    } from "../../../Components/Common/index.svelte";

    import {
        editIcon,
        MaxPaperProcessingFee,
    } from "../../../Utils/utilConstants.svelte";

    import {
        cachePreOpQuestionnaire,
        getFilteredPreOpInstructions,
    } from "../../../Utils/utilCache.svelte";
    import InfoIcon from "../../../Components/Common/InfoIcon.svelte";

    export let groupDetails = {};

    $: isValidContactEmail = groupDetails.contactEmail
        ? getEmailValidation(groupDetails.contactEmail)
        : true;

    $: isValidContactPhone = groupDetails.contactPhone
        ? getPhoneValidation(groupDetails.contactPhone) ||
          getLandlineValidation(groupDetails.contactPhone) ||
          getTollFreeValidation(groupDetails.contactPhone)
        : true;

    $: isValidCustomerFacingEmail = groupDetails.customerFacingEmail
        ? getEmailValidation(groupDetails.customerFacingEmail)
        : true;

    $: isValidRefundsEmail = groupDetails.refundsEmail
        ? getEmailValidation(groupDetails.refundsEmail)
        : true;

    $: isValidANB = groupDetails.abn
        ? checkAbnValidation(groupDetails.abn)
        : true;

    $: isValidBSB = groupDetails.bankAccountBSB
        ? BsbRegex.test(groupDetails.bankAccountBSB)
        : true;

    $: isValidAccountNumber = groupDetails.bankAccountNumber
        ? accountNumberRegex.test(groupDetails.bankAccountNumber)
        : true;

    $: isValidAdminEmail = groupDetails.adminEmail
        ? getEmailValidation(groupDetails.adminEmail)
        : true;

    $: isValidSupportEmail = groupDetails.supportEmail
        ? getEmailValidation(groupDetails.supportEmail)
        : true;

    $: isSubmitBtnDisabled = isUpdatingGroupDetails
        ? true
        : _getSubmitBtnDisabled(groupDetails);

    $: preOpQuestionnaireName =
        groupDetails.preOpQuestionnaire &&
        filteredPreOpQuestionnaires.length &&
        filteredPreOpQuestionnaires.some(
            (p) => p.id === groupDetails.preOpQuestionnaire,
        )
            ? filteredPreOpQuestionnaires.find(
                  (p) => p.id === groupDetails.preOpQuestionnaire,
              ).name
            : "None";

    $: preOpInstructionName =
        groupDetails.preOpInstructions &&
        filteredPreOpInstructions.length &&
        filteredPreOpInstructions.some(
            (i) => i.id == groupDetails.preOpInstructions,
        )
            ? filteredPreOpInstructions.find(
                  (i) => i.id == groupDetails.preOpInstructions,
              ).name
            : "None";

    let isEditEnabled = false,
        isUpdatingGroupDetails = false;

    let filteredPreOpQuestionnaires = [],
        isLoadingPreOpQuestionnaires = false;

    let filteredPreOpInstructions = [],
        isLoadingPreOpInstructions = false;

    const BsbRegex = /^\d{6}$/;
    const accountNumberRegex = /^\d{6,10}$/;

    $: btnOptions = [
        {
            iconName: "editIcon",
            iFont: editIcon,
            iconFunction: () => triggerEditEnable(),
            hasTooltip: false,
            btnText: "Edit",
            isHidden: isEditEnabled,
        },
    ];

    const dispatch = createEventDispatcher();

    onMount(async () => {
        await loadPreOpQuestionnaires();

        await loadPreOpInstructions();
    });

    const loadPreOpQuestionnaires = async () => {
        isLoadingPreOpQuestionnaires = true;

        await cachePreOpQuestionnaire();

        // wait store reactive update finish
        await tick();

        filteredPreOpQuestionnaires = $preOpQuestionnaires.filter(
            (questionnaire) =>
                questionnaire.groupId == groupDetails.id ||
                !questionnaire.groupId,
        );

        isLoadingPreOpQuestionnaires = false;
    };

    const loadPreOpInstructions = async () => {
        isLoadingPreOpInstructions = true;

        filteredPreOpInstructions = await getFilteredPreOpInstructions({
            groupId: groupDetails.id,
            checkGroupOnly: true,
        });

        isLoadingPreOpInstructions = false;
    };

    const updateGroupDetails = async () => {
        isUpdatingGroupDetails = true;

        _syncPercentageFee();

        await apiMethods.putMethod
            .updateGroup(groupDetails)
            .then((res) => _handleUpdateGroup(res))
            .catch((err) => {
                showException(err);
                dispatch("cancelEdit");
            });

        isUpdatingGroupDetails = false;
    };

    const cancelEdit = () => {
        isEditEnabled = false;
        dispatch("cancelEdit");
    };

    const triggerEditEnable = () => (isEditEnabled = true);

    // HELPER FUNCTIONS
    const _getSubmitBtnDisabled = (obj) => {
        return (
            !obj.name ||
            !isValidContactEmail ||
            !isValidContactPhone ||
            !isValidCustomerFacingEmail ||
            !isValidRefundsEmail ||
            !isValidANB ||
            !isValidBSB ||
            !isValidAccountNumber ||
            !isValidAdminEmail ||
            !isValidSupportEmail
        );
    };

    const _getPercentageDisplay = (value) => {
        if (!value) return "0 %";
        return getText(value) + " %";
    };

    const _handleUpdateGroup = (res) => {
        if (!res || res.error || !res.id) {
            showError(
                "Failed to update group details, please refresh the page and try again and contact support if the issue persists.",
            );
            dispatch("cancelEdit");
        } else {
            dispatch("saveEdit", res);
            isEditEnabled = false;
        }
    };

    const _syncPercentageFee = () => {
        if (!groupDetails.feePrivate) groupDetails.feePrivate = Number(0);
        else
            groupDetails.feePrivate = groupDetails.feePrivate
                .toString()
                .replace(" %", "");

        if (!groupDetails.feePublicInPrivate)
            groupDetails.feePublicInPrivate = Number(0);
        else
            groupDetails.feePublicInPrivate = groupDetails.feePublicInPrivate
                .toString()
                .replace(" %", "");
    };
</script>

<CardLayout title="Group Details" {btnOptions} enableCollapse={false}>
    <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex gap-x-2 items-center">
                <p class="title">Name:</p>
                {#if isEditEnabled}
                    <input
                        type="text"
                        class="content input input-sm input-bordered max-w-xs"
                        autocomplete="off"
                        bind:value={groupDetails.name}
                        disabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.name)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Contact Name:</p>
                {#if isEditEnabled}
                    <input
                        type="text"
                        class="content input input-sm input-bordered max-w-xs"
                        autocomplete="off"
                        bind:value={groupDetails.contactName}
                        disabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.contactName)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">
                    <InfoIcon info={`This email will be used for internal communications with the group`}></InfoIcon>
                    Contact Email:
                </p>
                {#if isEditEnabled}
                    <div class="content flex flex-col gap-y-2">
                        <input
                            type="text"
                            class="w-full input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.contactEmail}
                            disabled={isUpdatingGroupDetails}
                        />

                        {#if !isValidContactEmail}
                            <p class="text-error">Invalid Email</p>
                        {/if}
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.contactEmail)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">
                    Contact Phone:
                </p>
                {#if isEditEnabled}
                    <div class="content flex flex-col gap-y-2">
                        <input
                            type="text"
                            class="w-full input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.contactPhone}
                            disabled={isUpdatingGroupDetails}
                        />

                        {#if !isValidContactPhone}
                            <p class="text-error">Invalid Phone</p>
                        {/if}
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.contactPhone)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Address:</p>
                {#if isEditEnabled}
                    <input
                        type="text"
                        class="content input input-sm input-bordered max-w-xs"
                        autocomplete="off"
                        bind:value={groupDetails.address}
                        disabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.address)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title flex-0">Pre-Op Questionnaire:</p>

                {#if isLoadingPreOpQuestionnaires}
                    <div class="flex gap-x-2">
                        <Loader />
                        <p>Loading Pre-Op Questionnaire</p>
                    </div>
                {:else if isEditEnabled}
                    <select
                        class="content input input-sm input-bordered max-w-xs"
                        bind:value={groupDetails.preOpQuestionnaire}
                    >
                        <option
                            value={Number(0)}
                            selected={!groupDetails.preOpQuestionnaire}
                        >
                            None
                        </option>
                        {#each filteredPreOpQuestionnaires as { id, name }}
                            <option
                                value={id}
                                selected={groupDetails.preOpQuestionnaire == id}
                            >
                                {name}
                            </option>
                        {/each}
                    </select>
                {:else if preOpQuestionnaireName}
                    <p class="content truncate">
                        {getText(preOpQuestionnaireName)}
                    </p>
                {:else}
                    <p class="content">None</p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title flex-0">Default Pre-Op Instructions</p>
                {#if isLoadingPreOpInstructions}
                    <div class="flex gap-x-2">
                        <Loader />
                        <p>Loading Pre-Op Instructions</p>
                    </div>
                {:else if isEditEnabled}
                    <select
                        class="content input input-sm input-bordered max-w-xs"
                        bind:value={groupDetails.preOpInstructions}
                    >
                        <option value={Number(0)}> None </option>
                        {#each filteredPreOpInstructions as { id, name }}
                            <option
                                value={id}
                                selected={groupDetails.preOpInstructions == id}
                            >
                                {name}
                            </option>
                        {/each}
                    </select>
                {:else if preOpInstructionName}
                    <p class="content truncate">
                        {getText(preOpInstructionName)}
                    </p>
                {:else}
                    <p class="content">-</p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">PreOp Questionnaire Notification Days:</p>
                {#if isEditEnabled}
                    <NumericInput
                        bind:value={groupDetails.preOpQuestionnaireNotifyDays}
                        inputClass="w-[calc(100%-150px)] max-w-xs"
                        isDisable={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.preOpQuestionnaireNotifyDays)}
                    </p>
                {/if}
            </div>

            <div class="flex flex-col gap-y-4 items-start">
                <p class="w-full">PreOp Questionnaire Text in Communication:</p>
                {#if isEditEnabled}
                    <input
                        type="text"
                        class="input input-sm input-bordered w-full"
                        autocomplete="off"
                        bind:value={groupDetails.preOpQuestionnaireTextInComm}
                        disabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="w-full">
                        {getText(groupDetails.preOpQuestionnaireTextInComm)}
                    </p>
                {/if}
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">ABN:</p>
                {#if isEditEnabled}
                    <div class="content flex flex-col gap-y-2">
                        <input
                            type="text"
                            class="w-full input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.abn}
                            disabled={isUpdatingGroupDetails}
                        />

                        {#if !isValidANB}
                            <p class="text-error">Invalid ABN</p>
                        {/if}
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.abn)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Bank:</p>
                {#if isEditEnabled}
                    <input
                        type="text"
                        class="content input input-sm input-bordered max-w-xs"
                        autocomplete="off"
                        bind:value={groupDetails.bank}
                        disabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.bank)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Bank Code:</p>
                {#if isEditEnabled}
                    <input
                        type="text"
                        class="content input input-sm input-bordered max-w-xs"
                        autocomplete="off"
                        maxlength="3"
                        bind:value={groupDetails.bankCode}
                        disabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.bankCode)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Account Name:</p>
                {#if isEditEnabled}
                    <input
                        type="text"
                        class="content input input-sm input-bordered max-w-xs"
                        autocomplete="off"
                        bind:value={groupDetails.bankAccountName}
                        disabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.bankAccountName)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">
                    Bank Account BSB:
                </p>
                {#if isEditEnabled}
                    <div class="content input input-sm input-bordered max-w-xs">
                        <input
                            type="text"
                            class="w-full input max-w-xs input-xs"
                            autocomplete="off"
                            bind:value={groupDetails.bankAccountBSB}
                            disabled={isUpdatingGroupDetails}
                        />

                        {#if !isValidBSB}
                            <p class="text-error">Invalid BSB</p>
                        {/if}
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.bankAccountBSB)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">
                    Bank Account Number:
                </p>
                {#if isEditEnabled}
                    <div class="content flex flex-col gap-y-2">
                        <input
                            type="text"
                            class="w-full input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.bankAccountNumber}
                            disabled={isUpdatingGroupDetails}
                        />

                        {#if !isValidAccountNumber}
                            <p class="text-error">Invalid Number</p>
                        {/if}
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.bankAccountNumber)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Minor ID:</p>
                {#if isEditEnabled}
                    <input
                        type="text"
                        class="content input input-sm input-bordered max-w-xs"
                        autocomplete="off"
                        bind:value={groupDetails.minorId}
                        disabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.minorId)}
                    </p>
                {/if}
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex gap-x-2 items-center">
                <p class="title">Fee Private (%):</p>
                {#if isEditEnabled}
                    <PercentageInput
                        inputClass="w-[calc(100%-150px)] max-w-xs"
                        bind:value={groupDetails.feePrivate}
                        isDisabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {_getPercentageDisplay(groupDetails.feePrivate)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Fee Public in Private (%):</p>
                {#if isEditEnabled}
                    <PercentageInput
                        inputClass="w-[calc(100%-150px)] max-w-xs"
                        bind:value={groupDetails.feePublicInPrivate}
                        isDisabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {_getPercentageDisplay(groupDetails.feePublicInPrivate)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Fixed Fee per Doctor:</p>
                {#if isEditEnabled}
                    <DecimalInput
                        inputClass="w-[calc(100%-150px)] max-w-xs"
                        bind:value={groupDetails.fixedFeePerDoctor}
                        isDisabled={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getAmountInCurrency(groupDetails.fixedFeePerDoctor)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">
                    <InfoIcon info={`If Send Doctors Emails to Admin is turned on, some doctor emails will be sent to this address instead. This also receives some notifications including patient declined consent, partial payment, and claim declined`}></InfoIcon>
                    Admin Email:
                </p>
                {#if isEditEnabled}
                    <div class="content flex flex-col gap-y-2">
                        <input
                            type="text"
                            class="w-full input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.adminEmail}
                            disabled={isUpdatingGroupDetails}
                        />

                        {#if !isValidAdminEmail}
                            <p class="text-error">Invalid Email</p>
                        {/if}
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.adminEmail)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">
                    <InfoIcon info={`All doctor correspondence will be directed to the admin email`}></InfoIcon>
                    Send Doctors Emails to Admin:
                </p>
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.sendDoctorsEmailsToAdmin}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>

            <div class="flex gap-x-2 items-center items-center">
                <p class="title">Send Fortnightly Invoices:</p>
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.sendFortnightlyInvoice}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>

            <div class="flex gap-x-2 items-center items-center">
                <p class="title">
                    <InfoIcon info={`This applies to annual, quarterly and fortnightly account invoices`}></InfoIcon>
                    Send Doctors Invoices Directly to Doctors:
                </p>
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.sendDoctorsInvoicesToDoctors}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">
                    <InfoIcon info={`When adding a note, the "Push notification to doctor" toggle will be on by default`}></InfoIcon>
                    Note Push Notification to Doctor by Default:
                </p>
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.noteNotifyDoctorDefault}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>

            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">
                    <InfoIcon info={`Receives notifications including payment due, and patient update details`}></InfoIcon>
                    Support Email:
                </p>
                {#if isEditEnabled}
                    <div class="content flex flex-col gap-y-2">
                        <input
                            type="text"
                            class="w-full input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.supportEmail}
                            disabled={isUpdatingGroupDetails}
                        />

                        {#if !isValidSupportEmail}
                            <p class="text-error">Invalid Email</p>
                        {/if}
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.supportEmail)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">
                    <InfoIcon info={`This is used in all email communications to patients in the email signature`}></InfoIcon>
                    Customer Facing Email:
                </p>
                {#if isEditEnabled}
                    <div class="content flex flex-col gap-y-2">
                        <input
                            type="text"
                            class="w-full input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.customerFacingEmail}
                            disabled={isUpdatingGroupDetails}
                        />

                        {#if !isValidCustomerFacingEmail}
                            <p class="text-error">Invalid Email</p>
                        {/if}
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.customerFacingEmail)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">
                    <InfoIcon info={`This is used for emails relating to refunds`}></InfoIcon>
                    Refunds Email:
                </p>
                {#if isEditEnabled}
                    <div class="content flex flex-col gap-y-2">
                        <input
                            type="text"
                            class="w-full input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            bind:value={groupDetails.refundsEmail}
                            disabled={isUpdatingGroupDetails}
                        />

                        {#if !isValidRefundsEmail}
                            <p class="text-error">Invalid Email</p>
                        {/if}
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.refundsEmail)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2">
                <p class="title {isEditEnabled ? 'pt-1' : ''}">
                    SMS Originator:
                </p>
                {#if isEditEnabled}
                    <div class="content flex flex-col gap-y-2">
                        <input
                            type="text"
                            class="w-full input input-sm input-bordered max-w-xs"
                            autocomplete="off"
                            maxlength="11"
                            bind:value={groupDetails.smsOriginator}
                            disabled={isUpdatingGroupDetails}
                        />
                    </div>
                {:else}
                    <p class="content">
                        {getText(groupDetails.smsOriginator)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Avoid Using Plus Sign in Emails:</p>
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.avoidPlusInEmails}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>

            <div class="flex gap-x-2 items-center items-center">
                <p class="title">Add Phone to Notifications:</p>
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.addPhoneToComms}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>
        </div>
    </div>

    <div class="divider" />

    <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex gap-x-2 items-center">
                <p class="title">Hand Over:</p>

                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.handOver}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Auto Reconcile ERAs:</p>

                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.autoReconcileERAs}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Patient Pay Directly to Doctors:</p>

                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.patientPaymentsDirectlyToDoctors}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Insurance Funds Pay Directly to Doctors:</p>

                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={groupDetails.insurancePaymentsDirectlyToDoctors}
                    disabled={isUpdatingGroupDetails || !isEditEnabled}
                />
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex gap-x-2 items-center">
                <p class="title">
                    <InfoIcon info={`If a due date is not set in an encounter, and the quote is for prepayment, the due date will be set ${getText(groupDetails.estimatesDefaultDueDays)} business days before the scheduled procedure date`}></InfoIcon>
                    Quotes Default Due Days:
                </p>
                {#if isEditEnabled}
                    <NumericInput
                        bind:value={groupDetails.estimatesDefaultDueDays}
                        inputClass="flex-1"
                        isDisable={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.estimatesDefaultDueDays)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">
                    <InfoIcon info={`After sending out a fee estimate, the receiver will have ${groupDetails.quotesOnlyDefaultDueDays} calendar days to accept the estimate`}></InfoIcon>
                    Fee Estimates Default Due Days:
                </p>
                {#if isEditEnabled}
                    <NumericInput
                        bind:value={groupDetails.quotesOnlyDefaultDueDays}
                        inputClass="flex-1"
                        isDisable={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.quotesOnlyDefaultDueDays)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">
                    <InfoIcon info={`After changing an encounter’s status to Submitted, the system will automatically increment the status to In Progress, claim if appropriate, and request payment from the patient, if appropriate, after ${groupDetails.claimDelayHours} hours`}></InfoIcon>
                    Default Claim Delay (Hours):
                </p>
                {#if isEditEnabled}
                    <NumericInput
                        bind:value={groupDetails.claimDelayHours}
                        min="1"
                        max="72"
                        showInvalid
                        inputClass="flex-1"
                        isDisable={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.claimDelayHours)}
                    </p>
                {/if}
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex gap-x-2 items-center">
                <p class="title">
                    <InfoIcon info={`When creating an invoice, the due date for any patient payments will be ${groupDetails.invoicesDefaultDueDays} calendar days in the future`}></InfoIcon>
                    Invoices Default Due Days:
                </p>
                {#if isEditEnabled}
                    <NumericInput
                        bind:value={groupDetails.invoicesDefaultDueDays}
                        inputClass="flex-1"
                        isDisable={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.invoicesDefaultDueDays)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">
                    <InfoIcon info={`When creating an invoice, the due date for any insurer payments will be ${groupDetails.insuranceInvoicesDefaultDueDays} calendar days in the future`}></InfoIcon>
                    Insurance Invoices Default Due Days:
                </p>
                {#if isEditEnabled}
                    <NumericInput
                        bind:value={groupDetails.insuranceInvoicesDefaultDueDays}
                        inputClass="flex-1"
                        isDisable={isUpdatingGroupDetails}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.insuranceInvoicesDefaultDueDays)}
                    </p>
                {/if}
            </div>

            <div class="flex gap-x-2 items-center">
                <p class="title">Paper Processing Fee:</p>
                {#if isEditEnabled}
                    <DecimalInput
                        bind:value={groupDetails.paperProcessingFee}
                        isDisable={isUpdatingGroupDetails}
                        inputClass={groupDetails.paperProcessingFee >
                        MaxPaperProcessingFee
                            ? "w-[calc(100%-150px)] max-w-xs input-error"
                            : "w-[calc(100%-150px)] max-w-xs"}
                    />
                {:else}
                    <p class="content">
                        {getText(groupDetails.paperProcessingFee)}
                    </p>
                {/if}
            </div>

            {#if groupDetails.paperProcessingFee > MaxPaperProcessingFee}
                <div class="flex gap-x-2 items-center">
                    <p class="text-error">
                        The maximum allowed paper processing fee is {MaxPaperProcessingFee}
                    </p>
                </div>
            {/if}
        </div>
    </div>

    {#if isEditEnabled}
        <div class="flex flex-row items-center justify-end gap-x-4 pb-4 pr-4">
            <button
                class="btn btn-error btn-sm"
                on:click={cancelEdit}
                disabled={isUpdatingGroupDetails}
            >
                Cancel
            </button>
            <button
                class={`btn btn-secondary btn-sm ${
                    isUpdatingGroupDetails ? "loading" : ""
                }`}
                on:click={updateGroupDetails}
                disabled={isSubmitBtnDisabled}
            >
                {isUpdatingGroupDetails ? "Saving" : "Save"}
            </button>
        </div>
    {/if}
</CardLayout>

<style>
    .title {
        width: 150px;
    }

    .content {
        width: calc(100% - 150px);
    }

    .transition {
        transition: 0.5s linear;
    }
</style>
